import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Loader2, RefreshCw, Database } from 'lucide-react';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useRole } from '@/hooks/use-role';
import { useOrganizationMutations } from '../hooks/useOrganizationMutations';
import { ShopifyConnectionSection } from './ShopifyConnectionSection';
import { cn } from '@/lib/utils';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';

interface ShopifyIntegrationCardProps {
  /**
   * Suppress the card's own "Shopify Integration" title block. Set when the
   * card is embedded under a surface that already provides the heading (e.g.
   * the onboarding "Connect Shopify" step, whose StepPane header owns the
   * title) so the hierarchy isn't inverted by a larger inner title. Defaults
   * to false — the standalone Settings/Integrations usage is unchanged.
   */
  hideHeader?: boolean;
}

/**
 * Shopify Integration settings card.
 *
 * The top part is {@link ShopifyConnectionSection}: the store connection
 * (domain, Client ID, Client Secret), which also handles a Blue Monk custom
 * app. A platform admin (the platform admin list, BLU-1313; never the
 * 'Super Admin' role) gets the platform scope (custom app setup) for the
 * current org; everyone else gets the org scope. The rows below (sync
 * settings, product webhooks, inventory ID backfill) are unchanged.
 *
 * SECURITY: credentials are write-only and never read back from the vault.
 */
export function ShopifyIntegrationCard({ hideHeader = false }: ShopifyIntegrationCardProps = {}) {
  const { organization } = useOrganization();
  const { isPlatformAdmin } = useRole();
  const navigate = useNavigate();
  const { backfillInventory } = useOrganizationMutations();

  const registerProductWebhooks = trpc.shopifyIntegration.registerProductWebhooks.useMutation({
    onSuccess: (data) => {
      // The 2026-08-13 outage was subscriptions pointing at a host nobody
      // looked at, so the resolved URL leads — it is the one thing an operator
      // can eyeball and immediately tell is wrong.
      toast.success(`Webhooks now point at ${data.callbackUrl}`, {
        description:
          `created: ${data.created}, re-pointed: ${data.updated}, ` +
          `already correct: ${data.existing}, removed: ${data.retired}, failed: ${data.failed}`,
      });
      if (data.duplicates.length > 0) {
        toast.warning(
          `${data.duplicates.length} duplicate subscription(s) could not be removed`,
          {
            description: data.duplicates
              .map((duplicate) => `${duplicate.topic} → ${duplicate.address ?? 'unknown address'}`)
              .join(', '),
          }
        );
      }
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to register webhooks');
    },
  });

  return (
    <Card>
      {!hideHeader && (
        <CardHeader>
          <CardTitle>Shopify Integration</CardTitle>
          <CardDescription>
            Configure your Shopify store connection
          </CardDescription>
        </CardHeader>
      )}
      {/* CardContent is `p-6 pt-0`; when the header is suppressed, restore the
          top padding so the form doesn't jam against the card's top edge. */}
      <CardContent className={cn('space-y-4', hideHeader && 'pt-6')}>
        {organization ? (
          <ShopifyConnectionSection
            organizationId={organization.id}
            mode={isPlatformAdmin() ? 'platform' : 'org'}
          />
        ) : (
          <Skeleton className="h-24 w-full" />
        )}

        {/* Sync Settings Link */}
        <div className="pt-4 border-t">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium mb-1">Sync Settings</h4>
              <p className="text-xs text-muted-foreground">
                Manage order sync and PAP product sync settings, run manual syncs, and view sync history.
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/stock-sync')}
              className="shrink-0"
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Manage Sync Settings
            </Button>
          </div>
        </div>

        {/* Register Shopify Product Webhooks */}
        <div className="pt-4 border-t">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <h4 className="text-sm font-medium mb-1">Register Product Webhooks</h4>
              <p className="text-xs text-muted-foreground">
                Recommended. Registers <code>products/create</code> so new PAP products sync immediately, and points existing subscriptions at the current address.
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => registerProductWebhooks.mutate()}
              disabled={registerProductWebhooks.isPending}
              className="shrink-0"
            >
              {registerProductWebhooks.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Registering...
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Register
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Backfill Inventory IDs */}
        <div className="pt-4 border-t">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <h4 className="text-sm font-medium mb-1">Backfill Inventory IDs</h4>
              <p className="text-xs text-muted-foreground">
                Fetch Shopify inventory item IDs for SKUs missing activation metadata. Required for availability toggles.
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => backfillInventory.mutate({})}
              disabled={backfillInventory.isPending}
              className="shrink-0"
            >
              {backfillInventory.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Backfilling...
                </>
              ) : (
                <>
                  <Database className="mr-2 h-4 w-4" />
                  Backfill IDs
                </>
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
